/**
 * Search (PLAN.md WP0.6, hybrid retrieval in WP1.2): Swedish full-text search with entity
 * filter chips and marked hits, plus a semantic leg over the two indexes the user may read.
 *
 * The server marks hits with private-use-area characters; segmentHits() turns them into <mark>
 * elements here, so page content can never inject markup.
 *
 * The semantic toggle is offered only when the index can actually answer, and the answer says
 * which leg found each page — a result the words did not match needs to explain itself.
 */
import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

import {
  api,
  segmentHits,
  type SearchFacet,
  type SearchResponse,
  type Space,
} from "../../api";
import { EmptyState } from "../../components/EmptyState";
import { Card, Field, Num } from "../../components/primitives";
import { SkeletonText } from "../../components/Skeleton";

const DEBOUNCE_MS = 250;
// The choice survives reloads: search behaviour that changes under you is worse than either
// behaviour on its own.
const SEMANTIC_KEY = "noteshelf.search.semantic";

const KIND_LABELS: Record<string, string> = {
  project: "projekt",
  person: "person",
  company: "företag",
  regulation: "regelverk",
  other: "övrigt",
};

function Marked({ text }: { text: string }) {
  return (
    <>
      {segmentHits(text).map((segment, index) =>
        segment.hit ? (
          <mark
            key={index}
            className="rounded-[2px] text-ink"
            style={{ background: "color-mix(in srgb, var(--drop) 30%, transparent)" }}
          >
            {segment.text}
          </mark>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </>
  );
}

export function SokPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();

  const [query, setQuery] = useState(params.get("q") ?? "");
  const [spaceId, setSpaceId] = useState("");
  const [entityId, setEntityId] = useState<string | null>(null);
  const [spaces, setSpaces] = useState<Space[]>([]);
  const [response, setResponse] = useState<SearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  // Chips survive while the user retypes, so an active filter never flickers away.
  const [facets, setFacets] = useState<SearchFacet[]>([]);
  const [semantic, setSemantic] = useState(
    () => window.localStorage.getItem(SEMANTIC_KEY) !== "off",
  );

  useEffect(() => {
    void api.spaces().then(setSpaces);
  }, []);

  useEffect(() => {
    const q = query.trim();
    if (!q) {
      setResponse(null);
      setFacets([]);
      setEntityId(null);
      return;
    }
    setSearching(true);
    const timer = window.setTimeout(() => {
      void api
        .search({
          q,
          spaceId: spaceId || undefined,
          entityId: entityId ?? undefined,
          mode: semantic ? "hybrid" : "text",
        })
        .then((found) => {
          setResponse(found);
          setFacets(found.facets);
          setParams({ q }, { replace: true });
        })
        .finally(() => setSearching(false));
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [query, spaceId, entityId, semantic, setParams]);

  const state = useMemo(() => {
    if (!query.trim()) {
      return "idle";
    }
    if (response === null) {
      return "loading";
    }
    return response.results.length === 0 ? "empty" : "results";
  }, [query, response]);

  return (
    <div className="grid content-start gap-s6">
      <Card hero>
        <div className="flex flex-wrap items-end gap-s5">
          <div className="min-w-[260px] flex-1">
            <Field
              label="Sök"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              help={
                semantic
                  ? "Svensk fulltextsökning plus betydelse: citattecken för fraser, minus utesluter ord."
                  : "Svensk fulltextsökning: citattecken för fraser, minus utesluter ord."
              }
              autoFocus
            />
          </div>
          <label className="grid gap-s2">
            <span className="nb-overline border-0 pb-0">Utrymme</span>
            <select
              className="nb-field"
              value={spaceId}
              onChange={(event) => setSpaceId(event.target.value)}
            >
              <option value="">Alla utrymmen</option>
              {spaces.map((space) => (
                <option key={space.id} value={space.id}>
                  {space.kind === "private" ? `${space.name} (privat)` : space.name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-s3 pb-s2">
            <input
              type="checkbox"
              className="size-4 accent-[var(--deep)]"
              checked={semantic}
              onChange={(event) => {
                setSemantic(event.target.checked);
                window.localStorage.setItem(SEMANTIC_KEY, event.target.checked ? "on" : "off");
              }}
            />
            <span className="text-small text-ink">Sök även på betydelse</span>
          </label>
        </div>

        {semantic && response && response.semantic.unavailable_reason && (
          <p className="mt-s4 text-small text-muted">{response.semantic.unavailable_reason}</p>
        )}

        {facets.length > 0 && (
          <ul className="mt-s5 flex flex-wrap gap-s3" aria-label="Filtrera på entitet">
            {facets.map((facet) => {
              const active = facet.entity_id === entityId;
              return (
                <li key={facet.entity_id}>
                  <button
                    type="button"
                    aria-pressed={active}
                    className={`flex items-center gap-s3 rounded-control border px-s4 py-s2 text-small transition-[background,border-color] duration-control ease-nb ${
                      active
                        ? "border-drop bg-surface-2 font-medium text-ink"
                        : "border-line text-muted hover:bg-surface-2"
                    }`}
                    onClick={() => setEntityId(active ? null : facet.entity_id)}
                  >
                    <span>{facet.name}</span>
                    <span className="text-muted">
                      {KIND_LABELS[facet.kind] ?? facet.kind} · <Num>{facet.count}</Num>
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {state === "idle" && (
        <EmptyState
          title="Sök i allt du har tillgång till"
          description="Sidor och projektsidor i delade utrymmen och dina privata anteckningar. Träffarna markeras och kan filtreras per projekt eller annan entitet."
        />
      )}

      {state === "loading" && <SkeletonText lines={5} />}

      {state === "empty" && (
        <EmptyState
          title="Inga träffar"
          description={
            entityId
              ? "Inget matchar med det aktiva entitetsfiltret — prova att ta bort chippen."
              : "Prova en annan ordform eller färre ord. Sökningen förstår svenska böjningar."
          }
        />
      )}

      {state === "results" && response && (
        <>
          <p aria-live="polite" className="text-small text-muted">
            <Num>{response.total}</Num> {response.total === 1 ? "träff" : "träffar"}
            {searching ? " …" : ""}
          </p>
          <ul className="grid gap-s4">
            {response.results.map((result) => (
              <li key={result.page_id}>
                <Card
                  role="button"
                  tabIndex={0}
                  className="cursor-pointer transition-[box-shadow] duration-control ease-nb hover:shadow-dialog"
                  onClick={() => navigate(`/sidor/${result.page_id}`)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      navigate(`/sidor/${result.page_id}`);
                    }
                  }}
                >
                  <div className="flex flex-wrap items-baseline gap-s4">
                    <span className="text-title font-medium text-ink">
                      <Marked text={result.title} />
                    </span>
                    {!result.sources.includes("text") && (
                      <span className="text-small text-muted">funnen på betydelse</span>
                    )}
                    <span className="ml-auto text-small text-muted">{result.space_name}</span>
                  </div>
                  {result.snippet && (
                    <p className="mt-s2 text-body text-muted">
                      <Marked text={result.snippet} />
                    </p>
                  )}
                </Card>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}
